import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { authAPI } from '../services/api';
import { AuthLayout, TextInput, Button, Alert } from '../components';

export const VerifyOtpPage: React.FC = () => {
  const { t } = useTranslation();
  const [emailOrPhone, setEmailOrPhone] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const stored = sessionStorage.getItem('resetEmailOrPhone');
    if (stored) {
      setEmailOrPhone(stored);
    } else {
      navigate('/forgot-password');
    }
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!/^\d{6}$/.test(otpCode)) {
      setError(t('auth.otpMustBeSixDigits', { ns: 'validation' }));
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t('auth.passwordsDoNotMatch', { ns: 'validation' }));
      return;
    }

    if (newPassword.length < 8) {
      setError(t('auth.passwordMinLength', { ns: 'validation' }));
      return;
    }

    setIsLoading(true);

    try {
      await authAPI.verifyOtp({
        emailOrPhone,
        otpCode,
        newPassword,
      });

      sessionStorage.removeItem('resetEmailOrPhone');
      navigate('/login', { state: { message: t('verifyOtp.successMessage', { ns: 'auth' }) } });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('verifyOtp.resetFailed', { ns: 'auth' }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout
      title={t('verifyOtp.title', { ns: 'auth' })}
      subtitle={t('verifyOtp.subtitle', { ns: 'auth' })}
    >
      <form className="space-y-6" onSubmit={handleSubmit}>
        {error && <Alert variant="error" message={error} dismissible onDismiss={() => setError('')} />}

        <div className="rounded-lg border-2 border-[#d4cec4] bg-[#efe6d6] p-4 dark:border-[#6b6560] dark:bg-[#4a4339]">
          <p className="text-sm text-[#2b2b2b] dark:text-[#fffdf8]">
            {t('verifyOtp.otpSent', { ns: 'auth' })} <span className="font-semibold">{emailOrPhone}</span>
          </p>
        </div>

        <TextInput
          id="otpCode"
          label={t('verifyOtp.otpLabel', { ns: 'auth' })}
          type="text"
          inputMode="numeric"
          placeholder={t('verifyOtp.otpPlaceholder', { ns: 'auth' })}
          value={otpCode}
          onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          maxLength={6}
          required
        />

        <TextInput
          id="newPassword"
          label={t('verifyOtp.newPasswordLabel', { ns: 'auth' })}
          type="password"
          placeholder={t('verifyOtp.newPasswordPlaceholder', { ns: 'auth' })}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          required
          autoComplete="new-password"
        />

        <TextInput
          id="confirmPassword"
          label={t('verifyOtp.confirmPasswordLabel', { ns: 'auth' })}
          type="password"
          placeholder={t('verifyOtp.confirmPasswordPlaceholder', { ns: 'auth' })}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
          autoComplete="new-password"
        />

        <div className="text-sm text-[#78716c] dark:text-[#b0a8a0]">
          <p>{t('verifyOtp.passwordRequirementsTitle', { ns: 'auth' })}</p>
          <ul className="mt-2 list-inside list-disc space-y-1">
            <li>{t('verifyOtp.passwordRequirementLength', { ns: 'auth' })}</li>
            <li>{t('verifyOtp.passwordRequirementMatch', { ns: 'auth' })}</li>
          </ul>
        </div>

        <Button type="submit" variant="primary" size="md" fullWidth isLoading={isLoading}>
          {isLoading ? t('verifyOtp.submitLoading', { ns: 'auth' }) : t('verifyOtp.submitIdle', { ns: 'auth' })}
        </Button>

        <div className="pt-4 text-center">
          <p className="text-base text-[#78716c] dark:text-[#b0a8a0]">
            {t('verifyOtp.resendPrompt', { ns: 'auth' })}{' '}
            <Link to="/forgot-password" className="font-semibold text-[#b64f42] dark:text-[#d4825a] hover:underline">
              {t('verifyOtp.tryAgain', { ns: 'auth' })}
            </Link>
          </p>
        </div>
      </form>
    </AuthLayout>
  );
};
